import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlayCircle, Lock, Clock, Globe, BarChart, CheckCircle2, Radio, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { StarRating, Money, Badge } from "@/components/common";
import { toast } from "sonner";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then(({ data }) => setC(data));
    if (user) api.get("/my/enrollments").then(({ data }) => setEnrolled(data.some((e) => e.course_id === id)));
  }, [id, user]);

  if (!c) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 border-4 border-amber border-t-transparent rounded-full animate-spin" /></div>;

  const price = c.discount_price || c.price;
  const buyNow = () => {
    if (!user) return navigate("/login");
    navigate("/checkout", { state: { items: [{ type: "course", id: c.id, title: c.title, price }] } });
  };

  return (
    <div data-testid="course-detail-page">
      <div className="viriks-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex gap-2 mb-4">
              <Badge tone="amber">{c.level}</Badge>
              {c.course_type === "live" && <Badge tone="crimson">LIVE BATCH</Badge>}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold leading-tight">{c.title}</h1>
            <p className="text-white/70 mt-4 text-lg">{c.short_description}</p>
            <div className="flex flex-wrap items-center gap-5 mt-6 text-sm">
              <StarRating value={c.rating} showValue /> <span className="text-white/60">({c.review_count} reviews)</span>
              <span className="flex items-center gap-1 text-white/70"><Globe size={15} /> {c.language}</span>
              <span className="flex items-center gap-1 text-white/70"><BarChart size={15} /> {c.enrolled_count} enrolled</span>
            </div>
            <p className="text-white/70 mt-4">Instructor: <span className="font-semibold text-white">{c.instructor_name}</span></p>
          </div>

          <div className="lg:row-span-2">
            <div className="bg-white rounded-2xl overflow-hidden text-navy shadow-2xl sticky top-24">
              <img src={c.thumbnail} alt={c.title} className="w-full aspect-video object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <span className="font-heading text-3xl font-black"><Money amount={price} /></span>
                  {c.discount_price > 0 && <span className="text-muted-foreground line-through"><Money amount={c.price} /></span>}
                </div>
                <div className="mt-5 space-y-2">
                  {enrolled ? (
                    <Button className="w-full viriks-navy text-white" onClick={() => navigate(`/learner/learn/${c.id}`)} data-testid="go-to-course-btn">Go to Course</Button>
                  ) : c.sale_blocked ? (
                    <div className="text-sm text-center border border-border rounded-md p-3 text-muted-foreground" data-testid="course-unavailable">This course is currently unavailable for purchase.</div>
                  ) : (
                    <>
                      <Button className="w-full viriks-amber text-white font-semibold hover:bg-amber/90" onClick={buyNow} data-testid="buy-now-btn">Buy Now</Button>
                      <Button variant="outline" className="w-full" onClick={() => addToCart({ type: "course", id: c.id, title: c.title, price, thumbnail: c.thumbnail })} data-testid="add-cart-btn">
                        <ShoppingCart size={16} className="mr-2" /> Add to Cart
                      </Button>
                    </>
                  )}
                </div>
                <div className="mt-6 space-y-2 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2"><Clock size={15} /> {c.validity_days >= 36500 ? "Lifetime" : `${c.validity_days} days`} validity</p>
                  <p className="flex items-center gap-2"><PlayCircle size={15} /> Full HD lectures + notes</p>
                  {c.course_type === "live" && <p className="flex items-center gap-2"><Radio size={15} /> Live interactive classes</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 lg:grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="mb-6">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="curriculum" data-testid="tab-curriculum">Curriculum</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {c.what_you_learn?.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-navy mb-4">What you'll learn</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {c.what_you_learn.map((w, i) => (
                      <div key={i} className="flex gap-2 text-sm"><CheckCircle2 size={18} className="text-emerald-500 shrink-0" /> {w}</div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h3 className="font-heading text-xl font-bold text-navy mb-4">Description</h3>
                <div className="prose text-navy/80 max-w-none whitespace-pre-line" dangerouslySetInnerHTML={{ __html: c.full_description }} />
              </div>
              {c.highlights?.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-navy mb-4">Course Highlights</h3>
                  <ul className="space-y-2">{c.highlights.map((h, i) => <li key={i} className="flex gap-2 text-sm"><CheckCircle2 size={18} className="text-amber shrink-0" /> {h}</li>)}</ul>
                </div>
              )}
              {c.requirements?.length > 0 && (
                <div data-testid="course-requirements">
                  <h3 className="font-heading text-xl font-bold text-navy mb-4">Requirements</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-navy/80">{c.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="curriculum">
              <Accordion type="multiple" className="space-y-3">
                {c.sections?.map((s) => (
                  <AccordionItem key={s.id} value={s.id} className="bg-white border border-border rounded-lg px-4">
                    <AccordionTrigger className="font-heading font-semibold">{s.title} <span className="text-xs text-muted-foreground ml-2">({s.lessons?.length || 0} lessons)</span></AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1">
                        {s.lessons?.map((l) => (
                          <div key={l.id} className="flex items-center gap-3 py-2 text-sm">
                            {l.is_preview ? <PlayCircle size={16} className="text-amber" /> : <Lock size={16} className="text-muted-foreground" />}
                            <span className="flex-1">{l.title}</span>
                            <span className="text-muted-foreground text-xs">{l.duration}</span>
                            {l.is_preview && <Badge tone="amber">Preview</Badge>}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>

            <TabsContent value="reviews">
              <ReviewsSection course={c} user={user} enrolled={enrolled} onAdded={() => api.get(`/courses/${id}`).then(({ data }) => setC(data))} />
            </TabsContent>

            <TabsContent value="faqs">
              {!c.faqs?.length && <p className="text-sm text-muted-foreground" data-testid="no-faqs">No FAQs yet.</p>}
              <Accordion type="single" collapsible className="space-y-3">
                {c.faqs?.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="bg-white border border-border rounded-lg px-4">
                    <AccordionTrigger>{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function ReviewsSection({ course, user, enrolled, onAdded }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post("/reviews", { type: "course", course_id: course.id, rating, comment });
      toast.success("Review submitted for approval");
      setComment("");
      onAdded();
    } catch (e) { toast.error("Failed to submit review"); }
  };
  return (
    <div className="space-y-6">
      {enrolled && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h4 className="font-semibold text-navy mb-3">Write a review</h4>
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} onClick={() => setRating(i)}><StarRating value={i <= rating ? 5 : 0} /></button>
            ))}
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience..." className="w-full border border-border rounded-md p-3 text-sm" rows={3} data-testid="review-comment" />
          <Button className="mt-3 viriks-navy text-white" onClick={submit} data-testid="submit-review-btn">Submit Review</Button>
        </div>
      )}
      {course.reviews?.length === 0 && <p className="text-muted-foreground">No reviews yet.</p>}
      {course.reviews?.map((r) => (
        <div key={r.id} className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full viriks-navy text-amber flex items-center justify-center font-bold">{r.user_name?.[0]}</div>
            <div><div className="font-semibold text-sm">{r.user_name}</div><StarRating value={r.rating} size={12} /></div>
          </div>
          <p className="text-navy/80 mt-3 text-sm">{r.comment}</p>
        </div>
      ))}
    </div>
  );
}
